import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCommandPalette } from '../context/CommandPaletteContext';
import config from '../config/config';
import { authFetch } from '../utils/api';
import './CommandPalette.css';

// ── Static items ──────────────────────────────────────────────────────────────
const STATIC_ITEMS = [
  { id: 'nav-dashboard',  icon: '📊', label: 'Dashboard',           category: 'Navigate', action: '/', type: 'nav' },
  { id: 'nav-repos',      icon: '📦',  label: 'Projects',        category: 'Navigate', action: '/repos', type: 'nav' },
  { id: 'nav-upload',     icon: '⬆️',  label: 'Upload SBOM',          category: 'Navigate', action: '/upload', type: 'nav' },
  { id: 'nav-packages',   icon: '📦', label: 'Package Search',        category: 'Navigate', action: '/packages', type: 'nav' },
  { id: 'nav-matrix',     icon: '📋', label: 'Compliance Matrix',     category: 'Navigate', action: '/matrix', type: 'nav' },
  { id: 'nav-supplier-trust', icon: '🤝', label: 'Supplier Trust',    category: 'Navigate', action: '/supplier-trust', type: 'nav' },
  { id: 'nav-ai',         icon: '🤖', label: 'AI Settings',           category: 'Navigate', action: '/ai/settings', type: 'nav' },
  { id: 'nav-docs',       icon: '📖', label: 'Documentation',         category: 'Navigate', action: '/docs', type: 'nav' },
  { id: 'nav-docs-intel', icon: '🧠', label: 'Intelligence Documentation', category: 'Navigate', action: '/docs/intelligence', type: 'nav' },
  { id: 'nav-docs-intel-roadmap', icon: '🗺️', label: 'Intelligence Rule Roadmap', category: 'Navigate', action: '/docs/intelligence/rule-roadmap', type: 'nav' },
  { id: 'nav-progress',   icon: '🧭', label: 'Product Progress',      category: 'Navigate', action: '/progress', type: 'nav' },
  { id: 'nav-roadmap',    icon: '🚀', label: 'Commerciële Roadmap',   category: 'Navigate', action: '/roadmap', type: 'nav' },
  { id: 'nav-admin',      icon: '⚙️', label: 'User Management',       category: 'Navigate', action: '/admin/users', type: 'nav' },
  { id: 'ext-swagger',    icon: '🔌', label: 'Open API Docs (Swagger)', category: 'External', action: `${config.BASE_URL}/docs`, type: 'external' },
  { id: 'theme-toggle',   icon: '🌙', label: 'Toggle Dark / Light Mode', category: 'Actions', type: 'action',
    action: () => {
      const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
      document.documentElement.setAttribute('data-bs-theme', isDark ? 'light' : 'dark');
      localStorage.setItem('darkMode', String(!isDark));
    }
  },
];

function fuzzy(query, text) {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

export default function CommandPalette() {
  const { open, closePalette } = useCommandPalette();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [repos, setRepos] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [reposError, setReposError] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Load repos once
  useEffect(() => {
    setReposError('');
    authFetch(`${config.BASE_URL}/repositories/local`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const arr = Array.isArray(data) ? data : (data.repositories || []);
        setRepos(arr.map(r => ({
          id: `repo-${r.name || r}`,
          icon: '🗂️',
          label: r.name || r,
          category: 'Projects',
          action: `/repos/${encodeURIComponent(r.name || r)}`,
          type: 'nav',
        })));
      })
      .catch((e) => {
        setRepos([]);
        setReposError(e.message || 'Failed to load repositories for the command palette.');
      });
  }, []);

  const allItems = [...STATIC_ITEMS, ...repos];

  const filtered = query
    ? allItems.filter(item => fuzzy(query, item.label) || fuzzy(query, item.category))
    : allItems;

  // Group by category
  const grouped = filtered.reduce((acc, item) => {
    (acc[item.category] = acc[item.category] || []).push(item);
    return acc;
  }, {});

  const flatFiltered = Object.values(grouped).flat();

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Keep activeIdx in range
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const execute = useCallback((item) => {
    if (!item) return;
    closePalette();
    if (item.type === 'nav') {
      navigate(item.action);
    } else if (item.type === 'external') {
      window.open(item.action, '_blank', 'noopener');
    } else if (item.type === 'action') {
      item.action();
    }
  }, [closePalette, navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flatFiltered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      execute(flatFiltered[activeIdx]);
    } else if (e.key === 'Escape') {
      closePalette();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector('.cp-item.active');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  useEffect(() => {
    closePalette();
  }, [location.pathname, location.search, closePalette]);

  if (!open) return null;

  let globalIdx = 0;

  return (
    <div className="cp-overlay" onClick={closePalette}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="cp-search-row">
          <span className="cp-search-icon">🔍</span>
          <input
            ref={inputRef}
            className="cp-input"
            placeholder="Search pages, repositories, actions..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="cp-esc-hint">esc</kbd>
        </div>

        {/* Results */}
        <div className="cp-list" ref={listRef}>
          {reposError && (
            <div className="cp-empty" style={{ color: '#f59e0b' }}>
              {reposError}
            </div>
          )}
          {flatFiltered.length === 0 && (
            <div className="cp-empty">No results for "{query}"</div>
          )}
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="cp-group">
              <div className="cp-group-label">{category}</div>
              {items.map(item => {
                const idx = globalIdx++;
                return (
                  <button
                    key={item.id}
                    className={`cp-item${idx === activeIdx ? ' active' : ''}`}
                    onClick={() => execute(item)}
                    onMouseEnter={() => setActiveIdx(idx)}
                  >
                    <span className="cp-item-icon">{item.icon}</span>
                    <span className="cp-item-label">{item.label}</span>
                    {item.type === 'external' && <span className="cp-item-ext">↗</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="cp-footer">
          <span><kbd>↑↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Select</span>
          <span><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
}
